import type { RelabelPreview } from "../../../api/profiles/websiteRelabelService.js";
import type { ManifestRow } from "./parseManifest.js";

/**
 * Reconciles the two sources a relabel row can be described from: the API's
 * content preview and the uploaded manifest's own `name`/`excerpt` columns.
 *
 * Both are needed. profiles-api deliberately sends no `preview.name` for
 * `firm_profiles` — it has no clean path to address-book company names — so
 * every firm row would otherwise be identified by nothing but a truncated
 * uuid. The manifest generator does know the name and writes it into the file,
 * which makes the file the only source for those rows.
 *
 * The API still wins wherever it speaks: it read the database just now, while
 * the file was generated at some earlier point and may describe a profile that
 * has since been renamed or rewritten.
 *
 * Everything resolved here can come from an uploaded file, so it is untrusted
 * on both counts: render it as plain text, and put it through `csvField` on the
 * way back out to a rollback manifest.
 */

/** Live preview name, else the manifest's, else undefined for the caller to dash out. */
export function resolveName(row: ManifestRow, preview?: RelabelPreview): string | undefined {
  return preview?.name ?? row.name;
}

export type RowContent = {
  /** Where the text came from: the API's field name, or "manifest" for the file's own copy. */
  label: string;
  excerpt: string;
};

/**
 * The excerpt to show, labelled with its origin. The label matters: manifest
 * text was true when the file was generated, API text is true now, and a
 * reviewer deselecting a flagged row should be able to tell which they read.
 */
export function resolveContent(row: ManifestRow, preview?: RelabelPreview): RowContent | null {
  if (preview) {
    return { label: preview.label || "Content", excerpt: preview.excerpt };
  }
  if (row.excerpt) {
    return { label: "manifest", excerpt: row.excerpt };
  }
  return null;
}
